import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Upload, RefreshCw, AlertTriangle, CheckCircle2, Clock, Landmark, Send, RotateCcw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { ImportLog } from "@/components/admin/ImportLog";
import { saveBlob } from "@/components/admin/PointsUploadPanel";
import { PageLoader, LoadError, EmptyState } from "@/components/admin/LoadState";
import { PERIOD_KIND, PERIOD_STATUS } from "@/components/admin/SubscriptionLedger";
import { fmtDate, fmtDateTime, fmtMoney, fmtNum, plural } from "@/lib/format";

const REQUEST_STATUS: Record<string, { label: string; cls: string }> = {
  pendiente: { label: "Por enviar", cls: "text-amber-800" },
  enviada: { label: "Enviada", cls: "text-emerald-800" },
  anulada: { label: "Sin efecto", cls: "text-slate-600" },
};
const ACTION: Record<string, { title: string; label: string; danger?: boolean; needsNote?: boolean }> = {
  pagado: { title: "¿Registrar el cobro como pagado?", label: "Registrar pagado" },
  fallido: { title: "¿Registrar el cobro como rechazado?", label: "Registrar rechazo", danger: true, needsNote: true },
  anulado: { title: "¿Anular el cobro?", label: "Anular cobro", danger: true, needsNote: true },
};

// Débito automático de Armony: seguimiento de cobros pendientes, archivo para Armony y carga de resultados.
export function DebitPanel() {
  const [data, setData] = useState<any>(null);
  const [failed, setFailed] = useState(false);
  const [estado, setEstado] = useState<"abiertos" | "todos">("abiertos");
  const [exporting, setExporting] = useState(false);
  const [resend, setResend] = useState(false);
  const [action, setAction] = useState<{ charge: any; result: string } | null>(null);
  const [note, setNote] = useState("");
  const [logKey, setLogKey] = useState(0);

  const load = () => {
    setFailed(false);
    return api.get("/admin/debits", { params: { estado } }).then((r) => setData(r.data)).catch(() => setFailed(true));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [estado]);

  const doExport = async () => {
    try {
      const { data: blob } = await api.post("/admin/debits/export", null, { params: { reenviar: resend }, responseType: "blob" });
      saveBlob(blob, `debitos_armony_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success("Archivo generado. Envíalo a Finanzas de Armony y carga aquí los resultados cuando los tengas.");
      setLogKey((k) => k + 1);
      await load();
    } catch (e: any) {
      // Con responseType blob el detalle del error llega como Blob.
      const detail = e?.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : apiError(e);
      toast.error(detail || "No se pudo generar el archivo");
      throw e;
    }
  };

  const doAction = async () => {
    if (!action) return;
    try {
      await api.post(`/admin/debits/charges/${action.charge.id}/result`, { result: action.result, note: note.trim() || null });
      toast.success("Cobro actualizado");
      await load();
    } catch (e) { toast.error(apiError(e)); throw e; }
  };

  const runCycle = async () => {
    try {
      const { data: r } = await api.post("/admin/debits/run");
      toast.success(`Listo: ${plural(r.processed, "plan revisado", "planes revisados")}${r.ended ? `, ${plural(r.ended, "terminó", "terminaron")}` : ""}.`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (failed) return <LoadError what="los débitos" onRetry={load} />;
  if (!data) return <PageLoader />;
  const st = data.stats;

  return (
    <div className="space-y-6" data-testid="debit-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Landmark} label="Planes por débito" value={fmtNum(st.subscribers)} />
        <Stat icon={Clock} label="Por cobrar" value={fmtMoney(st.pending_amount)} sub={`${plural(st.pending_count, "cobro", "cobros")} · ${fmtNum(st.unsent_count)} sin enviar`} />
        <Stat icon={AlertTriangle} label="Rechazados" value={fmtMoney(st.failed_amount)} sub={plural(st.failed_count, "cobro", "cobros")} tone={st.failed_count ? "bad" : undefined} />
        <Stat icon={Send} label="Novedades por enviar" value={fmtNum(st.pending_requests)} sub="altas, cambios y bajas" />
      </div>

      <section className="card-obsidian p-5 space-y-4" aria-labelledby="debit-send-title">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 id="debit-send-title" className="text-lg font-semibold">1. Enviar a Armony</h2>
            <p className="text-sm text-slate-600 max-w-2xl">
              El archivo trae los cobros a sumar al débito de cada cliente (con una referencia única) y las novedades de alta, cambio y baja.
              Cada cobro se envía una sola vez; si Armony lo rechazó o no respondió, puedes volver a incluirlo.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={runCycle} className="rounded-full border-slate-200 text-slate-700" data-testid="debit-run-cycle">
              <RefreshCw className="w-4 h-4 mr-1" aria-hidden="true" /> Poner al día
            </Button>
            <Button onClick={() => setExporting(true)} className="gold-gradient-bg text-white rounded-full" data-testid="debit-export">
              <FileSpreadsheet className="w-4 h-4 mr-1" aria-hidden="true" /> Generar archivo para Armony
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-600">
          {st.immediate_access ? "El plan se activa al pedirlo; el cobro queda pendiente hasta que Armony lo confirme." : "El plan se activa cuando Armony confirma el primer cobro."}{" "}
          Si un cobro enviado no se confirma en {st.grace_days} días, el plan del cliente termina.
        </p>
      </section>

      <ResultsUpload onDone={() => { load(); setLogKey((k) => k + 1); }} />

      <section className="card-obsidian overflow-hidden" aria-labelledby="debit-charges-title">
        <div className="px-5 pt-5 pb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 id="debit-charges-title" className="text-lg font-semibold">Cobros por débito</h2>
          <div role="tablist" aria-label="Filtrar cobros" className="inline-flex p-1 rounded-full bg-slate-100 text-sm">
            {(["abiertos", "todos"] as const).map((k) => (
              <button key={k} type="button" role="tab" aria-selected={estado === k} onClick={() => setEstado(k)} data-testid={`debit-filter-${k}`}
                className={`px-3 py-1.5 rounded-full font-medium ${estado === k ? "bg-navy text-white" : "text-slate-700"}`}>
                {k === "abiertos" ? "Pendientes y rechazados" : "Todos"}
              </button>
            ))}
          </div>
        </div>
        {data.charges.length === 0 ? (
          <div className="px-5 pb-5"><EmptyState icon={CheckCircle2}><span data-testid="debit-charges-empty">{estado === "abiertos" ? "No hay cobros pendientes ni rechazados." : "Todavía no hay cobros por débito."}</span></EmptyState></div>
        ) : (
          <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Cobros por débito">
            <table className="w-full text-sm min-w-[980px]" data-testid="debit-charges">
              <thead>
                <tr className="border-y border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
                  <th scope="col" className="text-left font-medium px-5 py-3">Cliente</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Concepto</th>
                  <th scope="col" className="text-right font-medium px-5 py-3">Monto</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Enviado</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Estado</th>
                  <th scope="col" className="px-5 py-3"><span className="sr-only">Acciones</span></th>
                </tr>
              </thead>
              <tbody>
                {data.charges.map((c: any) => {
                  const ps = PERIOD_STATUS[c.status] || { label: c.status, cls: "text-slate-600" };
                  const open = c.status === "pendiente" || c.status === "fallido";
                  return (
                    <tr key={c.id} className="border-b border-slate-100 last:border-0 align-top" data-testid="debit-charge-row">
                      <td className="px-5 py-3">
                        <Link to={`/admin/usuarios/${c.user_id}?tab=suscripcion`} className="font-medium text-slate-900 hover:underline">{c.name || "—"}</Link>
                        <p className="text-xs text-slate-600">Cédula {c.cedula || "—"}</p>
                      </td>
                      <td className="px-5 py-3 text-slate-700">
                        {c.concept}
                        <p className="text-xs text-slate-600">{PERIOD_KIND[c.kind] || c.kind} · <span className="font-mono">{c.reference}</span></p>
                      </td>
                      <td className="px-5 py-3 text-right font-mono">{fmtMoney(c.amount_total, c.currency)}</td>
                      <td className="px-5 py-3 text-slate-700 whitespace-nowrap">
                        {c.sent_at ? fmtDate(c.sent_at) : <span className="text-amber-800">Sin enviar</span>}
                        {c.attempts > 1 && <p className="text-xs text-slate-600">{fmtNum(c.attempts)} envíos</p>}
                      </td>
                      <td className={`px-5 py-3 text-xs font-medium ${ps.cls}`}>
                        {ps.label}
                        {c.failure_reason && c.status === "fallido" && <p className="text-slate-600 font-normal">{c.failure_reason}</p>}
                        {c.overdue && <p className="text-red-700">Fuera de plazo</p>}
                        {c.status === "pagado" && c.paid_at && <p className="text-slate-600 font-normal">{fmtDate(c.paid_at)}</p>}
                      </td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        {open && (
                          <div className="inline-flex gap-1">
                            <button type="button" onClick={() => { setNote(""); setAction({ charge: c, result: "pagado" }); }} className="text-xs px-2.5 py-1 rounded-full border border-emerald-600/40 text-emerald-800 hover:bg-emerald-50" data-testid="debit-mark-paid">Pagado</button>
                            {c.status === "pendiente" && <button type="button" onClick={() => { setNote(""); setAction({ charge: c, result: "fallido" }); }} className="text-xs px-2.5 py-1 rounded-full border border-red-600/40 text-red-700 hover:bg-red-50" data-testid="debit-mark-failed">Rechazado</button>}
                            <button type="button" onClick={() => { setNote(""); setAction({ charge: c, result: "anulado" }); }} className="text-xs px-2.5 py-1 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="debit-mark-void">Anular</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card-obsidian overflow-hidden" aria-labelledby="debit-requests-title">
        <h2 id="debit-requests-title" className="px-5 pt-5 pb-3 text-lg font-semibold">Novedades para Armony</h2>
        {data.requests.length === 0 ? (
          <p className="px-5 pb-5 text-sm text-slate-600">Todavía no hay altas, cambios ni bajas por débito.</p>
        ) : (
          <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Novedades para Armony">
            <table className="w-full text-sm min-w-[980px]" data-testid="debit-requests">
              <thead>
                <tr className="border-y border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
                  <th scope="col" className="text-left font-medium px-5 py-3">Pedida el</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Cliente</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Novedad</th>
                  <th scope="col" className="text-right font-medium px-5 py-3">Monto recurrente</th>
                  <th scope="col" className="text-right font-medium px-5 py-3">Cargo único</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Desde</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Estado</th>
                </tr>
              </thead>
              <tbody>
                {data.requests.map((r: any) => {
                  const rs = REQUEST_STATUS[r.status] || { label: r.status, cls: "text-slate-600" };
                  return (
                    <tr key={r.id} className="border-b border-slate-100 last:border-0">
                      <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{fmtDateTime(r.requested_at)}</td>
                      <td className="px-5 py-3"><Link to={`/admin/usuarios/${r.user_id}?tab=suscripcion`} className="font-medium text-slate-900 hover:underline">{r.name || "—"}</Link>
                        <p className="text-xs text-slate-600">Cédula {r.cedula || "—"}</p></td>
                      <td className="px-5 py-3 text-slate-700">
                        <strong className="font-medium">{r.kind_label}</strong>
                        <p className="text-xs text-slate-600">{r.kind === "baja" ? r.from_label : r.from_label ? `${r.from_label} → ${r.to_label}` : r.to_label}</p>
                      </td>
                      <td className="px-5 py-3 text-right font-mono">{r.kind === "baja" ? "—" : fmtMoney(r.to_amount, r.currency)}</td>
                      <td className="px-5 py-3 text-right font-mono">{r.one_time_amount ? fmtMoney(r.one_time_amount, r.currency) : "—"}</td>
                      <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{fmtDate(r.effective_at)}</td>
                      <td className={`px-5 py-3 text-xs font-medium ${rs.cls}`}>{rs.label}{r.sent_at && <p className="text-slate-600 font-normal">{fmtDate(r.sent_at)}</p>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="grid lg:grid-cols-2 gap-6">
        <ImportLog kind="debito_envio" refreshKey={logKey} title="Archivos enviados a Armony" />
        <ImportLog kind="debito_resultado" refreshKey={logKey} title="Resultados cargados" />
      </div>

      <ConfirmDialog
        open={exporting}
        onOpenChange={setExporting}
        testid="debit-export-confirm"
        title="¿Generar el archivo para Armony?"
        description={<>
          <p>Incluye {plural(st.unsent_count, "cobro sin enviar", "cobros sin enviar")} y {plural(st.pending_requests, "novedad", "novedades")}. Antes se ponen al día las renovaciones.</p>
          <p>Los cobros incluidos quedan marcados como enviados: el plazo de {st.grace_days} días para confirmarlos empieza hoy.</p>
        </>}
        confirmLabel="Generar y descargar"
        onConfirm={doExport}
      >
        <label className="flex items-start gap-2 text-sm text-slate-700 mt-2">
          <input type="checkbox" checked={resend} onChange={(e) => setResend(e.target.checked)} className="mt-0.5 accent-navy" data-testid="debit-export-resend" />
          Volver a incluir los cobros rechazados o que Armony no ha confirmado
        </label>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!action}
        onOpenChange={(o) => !o && setAction(null)}
        testid="debit-action-confirm"
        title={action ? ACTION[action.result].title : ""}
        danger={action ? ACTION[action.result].danger : false}
        description={action && <p>{action.charge.name}: {action.charge.concept}, {fmtMoney(action.charge.amount_total)}.{action.result === "anulado" ? " Un cobro anulado ya no se le pedirá a Armony ni cuenta como deuda." : ""}</p>}
        confirmLabel={action ? ACTION[action.result].label : ""}
        onConfirm={doAction}
      >
        {action && ACTION[action.result].needsNote && (
          <div className="mt-2">
            <Label htmlFor="debit-note" className="text-sm text-slate-700">Motivo (opcional)</Label>
            <Textarea id="debit-note" rows={2} maxLength={300} value={note} onChange={(e: any) => setNote(e.target.value)} className="mt-1.5" placeholder="Ej. Fondos insuficientes" />
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
}

function ResultsUpload({ onDone }: { onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const send = async (f: File, confirm: boolean) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/admin/debits/results?confirm=${confirm}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (confirm) { toast.success(`Resultados aplicados: ${fmtNum(data.paid)} cobrados y ${fmtNum(data.failed)} rechazados`); onDone(); }
    } catch (e) { toast.error(apiError(e)); if (confirm) throw e; }
    finally { setBusy(false); }
  };

  const template = async () => {
    try {
      const { data } = await api.get("/admin/debits/results-template", { responseType: "blob" });
      saveBlob(data, "plantilla_resultados_debito.xlsx");
    } catch { toast.error("No se pudo descargar la plantilla"); }
  };

  const loaded = !!result && result.loaded > 0;
  return (
    <section className="card-obsidian p-5 space-y-4" aria-labelledby="debit-results-title" data-testid="debit-results">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="debit-results-title" className="text-lg font-semibold">2. Cargar los resultados de Armony</h2>
          <p className="text-sm text-slate-600 max-w-2xl">Una fila por cobro: su referencia y si se cobró o se rechazó. Primero revisamos el archivo; nada cambia hasta que confirmes. Un cobro ya pagado no se vuelve a aplicar.</p>
        </div>
        <button type="button" onClick={template} className="text-sm text-navy hover:underline flex items-center gap-1" data-testid="debit-results-template">
          <Download className="w-3.5 h-3.5" aria-hidden="true" /> Descargar plantilla
        </button>
      </div>
      <div className="max-w-md">
        <Label htmlFor="debit-results-file" className="text-sm text-slate-700">Archivo de resultados (.xlsx o CSV)</Label>
        <Input id="debit-results-file" type="file" accept=".xlsx,.csv" disabled={busy || loaded} data-testid="debit-results-file" className="mt-1.5"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) { setFile(f); setResult(null); send(f, false); } }} />
        {file && <p className="text-xs text-slate-600 mt-1.5 font-mono">{file.name}</p>}
      </div>
      {result && (
        <div className="space-y-3" data-testid="debit-results-preview">
          <p className="text-sm text-slate-800">
            {fmtNum(result.total)} filas: <strong className="text-emerald-800">{fmtNum(result.paid)} cobrados ({fmtMoney(result.paid_amount)})</strong>,{" "}
            <strong className="text-red-700">{fmtNum(result.failed)} rechazados</strong> y {fmtNum(result.errors.length)} con error.
            {loaded && " Ya se aplicaron."}
          </p>
          {result.errors.length > 0 && (
            <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200">
              <table className="w-full text-xs">
                <thead className="sr-only"><tr><th>Fila</th><th>Referencia</th><th>Problema</th></tr></thead>
                <tbody>
                  {result.errors.map((e: any) => (
                    <tr key={e.row} className="border-b border-slate-200 last:border-0">
                      <td className="px-3 py-2 font-mono text-slate-600">Fila {e.row}</td>
                      <td className="px-3 py-2 font-mono text-slate-800">{e.reference || "—"}</td>
                      <td className="px-3 py-2 text-red-700">{e.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        {(file || loaded) && (
          <Button variant="outline" onClick={() => { setFile(null); setResult(null); }} className="rounded-full border-slate-200 text-slate-700">
            <RotateCcw className="w-4 h-4 mr-1" aria-hidden="true" /> {loaded ? "Cargar otro archivo" : "Empezar de nuevo"}
          </Button>
        )}
        {!loaded && (
          <Button onClick={() => setConfirming(true)} disabled={busy || !result?.valid} className="gold-gradient-bg text-white rounded-full" data-testid="debit-results-confirm">
            <Upload className="w-4 h-4 mr-1" aria-hidden="true" /> Aplicar {plural(result?.valid ?? 0, "resultado", "resultados")}
          </Button>
        )}
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        testid="debit-results-apply"
        title="¿Aplicar los resultados?"
        description={<p>Se registrarán {fmtNum(result?.paid)} cobros como pagados y {fmtNum(result?.failed)} como rechazados. Los clientes con un rechazo verán el aviso en su cuenta.</p>}
        confirmLabel="Aplicar resultados"
        onConfirm={() => send(file!, true)}
      />
    </section>
  );
}

const Stat = ({ icon: Icon, label, value, sub, tone }: any) => (
  <div className="card-obsidian p-5">
    <div className="flex items-center justify-between gap-2">
      <p className="text-sm text-slate-600">{label}</p>
      <Icon className={`w-4 h-4 shrink-0 ${tone === "bad" ? "text-red-700" : "text-navy"}`} aria-hidden="true" />
    </div>
    <p className={`text-2xl font-serif font-medium mt-2 ${tone === "bad" ? "text-red-700" : "text-slate-900"}`}>{value}</p>
    {sub && <p className="text-xs text-slate-600 mt-1">{sub}</p>}
  </div>
);
